import cn from "classnames";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { lazyWithPreload } from "utils/lazyWithPreload";

import { FeatureFlag } from "featureFlags/constants";
import { useAllFlags } from "featureFlags/hooks/useAllFlags";
import { usePreloadComponents } from "hooks/usePreloadedComponents";
import { useWorkspaceEngines } from "services/engines/useWorkspaceEngines";

import DocumentOutputCollpaseIcon from "assets/icons/DocumentOutputCollpaseIcon.svg?react";
import EngineMonitoringIcon from "assets/icons/EngineMonitoringIcon.svg?react";
import OutputTabIconResults from "assets/icons/OutputTabIconResults.svg?react";
import OutputTabIconStatistics from "assets/icons/OutputTabIconStatistics.svg?react";
import QueryHistoryIcon from "assets/icons/QueryHistoryIcon.svg?react";

import DocumentResults from "pages/DevelopWorkspace/Editor/Document/DocumentOutput/DocumentResults/DocumentResults";
import { useDocuments } from "pages/DevelopWorkspace/contexts/DocumentsContext/hooks/useDocuments";
import { SHORTCUTS } from "pages/DevelopWorkspace/services/shortcuts/constants";
import { getShortcutHint } from "pages/DevelopWorkspace/services/shortcuts/helpers/getShortcutHint";
import { ShortcutAction } from "pages/DevelopWorkspace/services/shortcuts/types";
import {
  DocumentOutputTab,
  ExecutionType,
  WorkspaceDocument,
} from "pages/DevelopWorkspace/workspace.types";

import { ExplainIcon } from "components/Icons";
import { useLayoutToggles } from "components/LayoutToggles/context";
import Tooltip from "components/Tooltip/Tooltip";

import styles from "./DocumentOutput.module.scss";

interface DocumentOutputProps {
  document: WorkspaceDocument;
}

type HeaderProps = {
  handleTabClick: (tab: DocumentOutputTab) => void;
  activeTab: DocumentOutputTab | false;
};

const ExplainResults = lazyWithPreload(
  () => import("./DocumentResults/ExplainResults")
);

const QueryHistory = lazyWithPreload(
  () =>
    import("pages/DevelopWorkspace/Editor/Document/DocumentOutput/QueryHistory/QueryHistory")
);

const EngineMonitoring = lazyWithPreload(
  () =>
    import("pages/DevelopWorkspace/Editor/Document/DocumentOutput/EngineMonitoring/EngineMonitoring")
);

const DocumentStatistics = lazyWithPreload(
  () =>
    import("pages/DevelopWorkspace/Editor/Document/DocumentOutput/DocumentStatistics/DocumentStatistics")
);

const ResultsHeader = (props: HeaderProps) => {
  const { handleTabClick, activeTab } = props;
  const flags = useAllFlags();
  const { t } = useTranslation();
  const isLite = Boolean(process.env.REACT_APP_LITE);
  const { layout, setLayout } = useLayoutToggles();

  const isQueryHistoryEnabled =
    flags[FeatureFlag.FireboltAppEnableEngineObservAndQueryHistory];
  const isEngineMonitoringEnabled =
    flags[FeatureFlag.FireboltAppEnableEngineObservAndQueryHistory] && !isLite;

  usePreloadComponents([
    ExplainResults,
    QueryHistory,
    EngineMonitoring,
    DocumentStatistics,
  ]);

  return (
    <div className={styles.tabsPanel}>
      <div className={styles.panel}>
        <div
          onClick={() => {
            handleTabClick(DocumentOutputTab.Results);
          }}
          data-testid="results-tab"
          className={cn(styles.tab, {
            [styles.active]: activeTab === DocumentOutputTab.Results,
          })}
        >
          <div className={styles.icon}>
            <OutputTabIconResults />
          </div>
          <div className={styles.label}>Results</div>
        </div>

        <div
          onClick={() => {
            handleTabClick(DocumentOutputTab.Statistics);
          }}
          className={cn(styles.tab, {
            [styles.active]: activeTab === DocumentOutputTab.Statistics,
          })}
          data-testid="statistics-tab"
        >
          <div className={styles.icon}>
            <OutputTabIconStatistics />
          </div>
          <div className={styles.label}>Statistics</div>
        </div>

        <div
          onClick={() => {
            handleTabClick(DocumentOutputTab.Explain);
          }}
          className={cn(styles.tab, {
            [styles.active]: activeTab === DocumentOutputTab.Explain,
          })}
          data-testid="query-plan-tab"
        >
          <div className={styles.icon}>
            <ExplainIcon />
          </div>
          <div className={styles.label}>Query Profile</div>
        </div>

        {isEngineMonitoringEnabled && (
          <div
            onClick={() => {
              handleTabClick(DocumentOutputTab.EngineMonitoring);
            }}
            className={cn(styles.tab, {
              [styles.active]: activeTab === DocumentOutputTab.EngineMonitoring,
            })}
            data-testid="engine-metering-tab"
          >
            <div className={styles.icon}>
              <EngineMonitoringIcon />
            </div>
            <div className={styles.label}>{t("engine_monitoring.title")}</div>
          </div>
        )}

        {isQueryHistoryEnabled && (
          <div
            onClick={() => {
              handleTabClick(DocumentOutputTab.QueryHistory);
            }}
            className={cn(styles.tab, {
              [styles.active]: activeTab === DocumentOutputTab.QueryHistory,
            })}
            data-testid="query-history-tab"
          >
            <div className={styles.icon}>
              <QueryHistoryIcon />
            </div>
            <div className={styles.label}>{t("query_history.title")}</div>
          </div>
        )}
      </div>

      <div className={styles.collapseIconContainer}>
        <Tooltip
          title={`${
            layout.results.expanded
              ? t("workspace.tooltips.hide_results")
              : t("workspace.tooltips.show_results")
          } (${getShortcutHint(
            SHORTCUTS[ShortcutAction.ToggleResultsSection]
          ).join(" + ")})`}
          placement="top-end"
        >
          <div
            className={cn(styles.collapseIcon, {
              [styles.active]: layout.results.expanded,
            })}
            onClick={() => {
              setLayout({
                ...layout,
                results: {
                  ...layout.results,
                  expanded: !layout.results.expanded,
                },
              });
            }}
          >
            <DocumentOutputCollpaseIcon />
          </div>
        </Tooltip>
      </div>
    </div>
  );
};

const ExplainHeader = () => {
  return (
    <div className={styles.tabsPanel}>
      <div className={cn(styles.tab, styles.active)}>
        <div className={styles.icon}>
          <ExplainIcon />
        </div>
        <div className={styles.label}>Query Plan</div>
      </div>
    </div>
  );
};

const Header = (props: {
  handleTabClick: (tab: DocumentOutputTab) => void;
  activeTab: DocumentOutputTab | false;
  executionType?: ExecutionType;
}) => {
  const { executionType, handleTabClick, activeTab } = props;
  if (executionType === ExecutionType.Explain) {
    return <ExplainHeader />;
  }
  return (
    <ResultsHeader
      handleTabClick={handleTabClick}
      activeTab={activeTab}
    />
  );
};

const DocumentOutput = (props: DocumentOutputProps) => {
  const { document } = props;
  const {
    actions: { switchActiveOutputTab, updateDocumentEngineMonitoring },
  } = useDocuments();
  const isLite = Boolean(process.env.REACT_APP_LITE);
  const { data: engines } = useWorkspaceEngines({
    includeSystemEngine: true,
  });
  const { layout, setLayout } = useLayoutToggles();
  const [expanded, setExpanded] = useState(false);

  const engineName = document.context.engine.name;
  const activeTab = layout.results.expanded && document.layout.activeOutputTab;

  const handleTabClick = (tab: DocumentOutputTab) => {
    setLayout({
      ...layout,
      results: {
        ...layout.results,
        expanded: true,
      },
    });

    switchActiveOutputTab({
      documentId: document.id,
      activeOutputTab: tab,
    });
  };

  const renderQueryHistory = () => {
    const currentEngine = engines?.find(
      engine => engine.engineName === engineName
    );

    if (!currentEngine) {
      return null;
    }

    return (
      <QueryHistory
        engine={currentEngine}
        document={document}
      />
    );
  };

  const renderEngineMonitoring = () => {
    const currentEngine = engines?.find(
      engine => engine.engineName === engineName
    );

    if (!currentEngine || isLite) {
      return null;
    }

    return (
      <EngineMonitoring
        engine={currentEngine}
        document={document}
        onSettingsChange={engineMonitoring => {
          updateDocumentEngineMonitoring(document.id, engineMonitoring);
        }}
      />
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case DocumentOutputTab.Results:
        return (
          <DocumentResults
            document={document}
            expanded={expanded}
            setExpanded={setExpanded}
          />
        );
      case DocumentOutputTab.Statistics:
        return <DocumentStatistics document={document} />;
      case DocumentOutputTab.Explain:
        return (
          <ExplainResults
            document={document}
            isProfile
            expanded={expanded}
            setExpanded={setExpanded}
          />
        );
      case DocumentOutputTab.QueryHistory:
        return renderQueryHistory();
      case DocumentOutputTab.EngineMonitoring:
        return renderEngineMonitoring();
      default:
        return null;
    }
  };

  return (
    <div className={cn(styles.root, { [styles.expanded]: expanded })}>
      <Header
        executionType={document?.execution?.executionType}
        handleTabClick={handleTabClick}
        activeTab={activeTab}
      />
      <div
        className={cn(styles.body, {
          [styles.hidden]: !layout.results.expanded,
        })}
      >
        {renderTabContent()}
      </div>
    </div>
  );
};

export default DocumentOutput;
